import React, { useState, useCallback, useEffect, useRef } from 'react'
import './BoardResizer.css'

const BoardResizer = ({ onSizeChange, initialSize = 400, minSize = 200, maxSize = 800 }) => {
  // Get device-specific initial size
  const getInitialBoardSize = () => {
    const savedSize = localStorage.getItem('chessBoardSize')
    if (savedSize) return parseInt(savedSize) // User preference takes priority

    const isMobile = window.innerWidth <= 768
    return isMobile ? 400 : 700 // Device-specific defaults
  }

  const [boardSize, setBoardSize] = useState(() => {
    return getInitialBoardSize()
  })
  const [isResizing, setIsResizing] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startY, setStartY] = useState(0)
  const [startSize, setStartSize] = useState(0)

  // Performance optimization: use refs to avoid re-renders during drag
  const currentSizeRef = useRef(boardSize)
  const animationFrameRef = useRef(null)

  // Save board size to localStorage whenever it changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('chessBoardSize', boardSize.toString())
    }, 100) // Debounce localStorage writes

    return () => clearTimeout(timeoutId)
  }, [boardSize])

  // Call onSizeChange immediately but don't cause re-renders
  useEffect(() => {
    if (onSizeChange) {
      onSizeChange(boardSize)
    }
    currentSizeRef.current = boardSize
  }, [boardSize, onSizeChange])

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    setIsResizing(true)
    setStartX(e.clientX)
    setStartY(e.clientY)
    setStartSize(boardSize)
    document.body.style.cursor = 'nw-resize'
    document.body.style.userSelect = 'none'

    // Disable CSS transitions during resize for instant feedback
    document.body.classList.add('resizing-board')
  }, [boardSize])

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return

    e.preventDefault()

    // Cancel previous animation frame to avoid queuing
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      // Calculate the distance from start position
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      // Use the larger absolute value for diagonal resizing
      // Negative deltas (left/up) = smaller, Positive deltas (right/down) = bigger
      const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY
      const newSize = Math.min(maxSize, Math.max(minSize, startSize + delta))

      // Only update state if size actually changed
      if (newSize !== currentSizeRef.current) {
        setBoardSize(newSize)
      }
    })
  }, [isResizing, startX, startY, startSize, minSize, maxSize])

  const handleMouseUp = useCallback(() => {
    if (!isResizing) return

    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    setIsResizing(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''

    // Re-enable CSS transitions after resize
    document.body.classList.remove('resizing-board')
  }, [isResizing])

  // Add global event listeners when resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  // Handle window resize for device orientation changes
  useEffect(() => {
    const handleWindowResize = () => {
      // Only adjust if user hasn't manually set a preference
      const savedSize = localStorage.getItem('chessBoardSize')
      if (!savedSize) {
        const isMobile = window.innerWidth <= 768
        const deviceDefaultSize = isMobile ? 400 : 700
        setBoardSize(deviceDefaultSize)
      }
    }

    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const handleDoubleClick = useCallback(() => {
    // Double-click to reset to device-specific default size
    const isMobile = window.innerWidth <= 768
    const deviceDefaultSize = isMobile ? 400 : 700
    setBoardSize(deviceDefaultSize)
  }, [])

  return (
    <div
      className={`board-resizer ${isResizing ? 'resizing' : ''}`}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      title="Drag to resize board (double-click to reset)"
    >
      <div className="resize-handle">
        <div className="resize-line"></div>
        <div className="resize-line"></div>
      </div>
    </div>
  )
}

export default BoardResizer